import { combineReducers, configureStore, type Middleware } from '@reduxjs/toolkit';

import baseApi from '@/app/store/api/baseApi';
import { servicePointChangeMiddleware } from '@/app/store/middleware/servicePointChangeMiddleware.ts';
import activeMenuReducer from '@/app/store/slices/activeMenuSlice';
import appConfigReducer from '@/app/store/slices/appConfigSlice';
import cacheReducer from '@/app/store/slices/cacheSlice';
import notificationInboxReducer from '@/app/store/slices/notificationInboxSlice';
import servicePointProfileReducer from '@/app/store/slices/servicePointProfileSlice';
import {
    initializeReduxBroadcastListener,
    stateBroadcast,
} from '@/app/store/stateBroadcast/stateBroadcast.ts';
import { hydrateSnapshot } from '@/app/store/stateSnapshot/hydrateSnapshotAction';
import { selectStateSnapshotSlices } from '@/app/store/stateSnapshot/stateSnapshotConfig';
import {
    clearStateSnapshot,
    initializeStateSnapshotPersistence,
    readStateSnapshot,
} from '@/app/store/stateSnapshot/stateSnapshotPersistence';
import courierHandoverReducer from '@/features/courierHandover/slices/courierHandoverSlice';
import actionFooterReducer from '@/features/firstMile/slices/actionFooterSlice';
import activeAccordionReducer from '@/features/firstMile/slices/activeAccordionSlice';
import customerDropoutReducer from '@/features/firstMile/slices/dropoutModalSlice';
import quotationAndShippingReducer from '@/features/firstMile/slices/quotationAndShippingSlice';
import shipmentSelectionReducer from '@/features/firstMile/slices/shipmentSelectionSlice';
import customerCollectionReducer from '@/features/lastMile/slices/customerCollectionSlice';

import authReducer from '../../features/auth/authSlice';

const rootReducer = combineReducers({
    activeMenuStore: activeMenuReducer,
    authStore: authReducer,
    appConfigStore: appConfigReducer,

    cache: cacheReducer,
    servicePointProfile: servicePointProfileReducer,
    notificationInbox: notificationInboxReducer,

    footerConfig: actionFooterReducer,
    quotationAndShipping: quotationAndShippingReducer,
    shipmentSelection: shipmentSelectionReducer,
    customerDropOut: customerDropoutReducer,
    activeAccordion: activeAccordionReducer,
    customerCollection: customerCollectionReducer,
    courierHandover: courierHandoverReducer,

    // API
    [baseApi.reducerPath]: baseApi.reducer,
});

type ReducerState = ReturnType<typeof rootReducer>;

/**
 * Middleware to clear the state snapshot on logout.
 * Prevents stale authenticated state from persisting after user signs out.
 */
const logoutClearSnapshotMiddleware: Middleware = () => (next) => (action) => {
    const result = next(action);
    if (
        typeof action === 'object' &&
        action !== null &&
        'type' in action &&
        action.type === 'auth/logout'
    ) {
        clearStateSnapshot();
    }
    return result;
};

export const store = configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) => {
        return getDefaultMiddleware().concat(
            logoutClearSnapshotMiddleware,
            servicePointChangeMiddleware,
            stateBroadcast,
            baseApi.middleware,
        );
    },
});

initializeReduxBroadcastListener(store.dispatch);

initializeStateSnapshotPersistence({
    store,
    selectPersistableState: selectStateSnapshotSlices,
});

// Read the AES-GCM snapshot and dispatch it so each slice can hydrate itself.
// Always dispatch — even with an empty payload — so slices can mark themselves
// as hydrated and unblock any UI that waits for snapshot resolution.
readStateSnapshot<Partial<ReducerState>>().then((snapshot) => {
    store.dispatch(hydrateSnapshot(snapshot ?? {}));
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
